import { useMemo, memo, useState } from 'react';
import useAppStore from '../stores/appStore';
import { getAudioTrack } from '../data/poems';
import { useMobile } from '../hooks/useMobile';
import './AudioController.css';

/**
 * AudioVisualizer - Memoized visualizer component
 * Uses CSS transforms for better performance
 * Always rendered but hidden when not active to maintain consistent layout
 */
const AudioVisualizer = memo(({ bass, mids, highs, amplitude, isHidden, compact = false }) => {
  const className = `audio-visualizer${isHidden ? ' hidden' : ''}${compact ? ' compact' : ''}`;
  return (
    <div className={className}>
      <div className="viz-bar" style={{ transform: `scaleY(${Math.min(1, bass)})` }} />
      <div className="viz-bar" style={{ transform: `scaleY(${Math.min(1, mids)})` }} />
      <div className="viz-bar" style={{ transform: `scaleY(${Math.min(1, highs)})` }} />
      <div className="viz-bar" style={{ transform: `scaleY(${Math.min(1, amplitude)})` }} />
    </div>
  );
});

AudioVisualizer.displayName = 'AudioVisualizer';

/**
 * Mobile Audio Controller - Compact floating bar
 */
function MobileAudioController({ audioPlaying, isAnalyzing, bass, mids, highs, amplitude, onPlayPause }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { audioVolume, setAudioVolume, currentChapter } = useAppStore();

  const track = currentChapter !== null ? getAudioTrack(currentChapter + 1) : null;
  const currentTrackName = track ? track.display : 'Ambient';

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setAudioVolume(newVolume);
  };

  return (
    <div className={`mobile-audio-controller ${isExpanded ? 'expanded' : ''}`}>
      {/* Main compact bar */}
      <div className="mobile-audio-bar">
        {/* Play/Pause Button */}
        <button
          className="mobile-audio-play-btn"
          onClick={onPlayPause}
          aria-label={audioPlaying ? 'Pause' : 'Play'}
        >
          {audioPlaying ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </button>

        {/* Mini Visualizer */}
        <AudioVisualizer
          bass={isAnalyzing ? bass : 0}
          mids={isAnalyzing ? mids : 0}
          highs={isAnalyzing ? highs : 0}
          amplitude={isAnalyzing ? amplitude : 0}
          isHidden={!isAnalyzing}
          compact={true}
        />

        {/* Track name (truncated) */}
        <div className="mobile-audio-track">
          <span className="mobile-track-name">{currentTrackName}</span>
          <span className="mobile-audio-status">
            {audioPlaying ? (isAnalyzing ? 'Playing' : 'Loading...') : 'Paused'}
          </span>
        </div>

        {/* Expand toggle */}
        <button
          className="mobile-audio-expand-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? 'Collapse controls' : 'Expand controls'}
          aria-expanded={isExpanded}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d={isExpanded ? "M7 14l5-5 5 5z" : "M7 10l5 5 5-5z"} />
          </svg>
        </button>
      </div>

      {/* Expanded controls */}
      {isExpanded && (
        <div className="mobile-audio-expanded">
          <div className="mobile-volume-control">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
            </svg>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={audioVolume}
              onChange={handleVolumeChange}
              className="mobile-volume-slider"
              aria-label="Volume"
            />
            <span className="mobile-volume-value">{Math.round(audioVolume * 100)}%</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * AudioController - UI controls for audio playback
 * The actual audio management happens in AudioManager (inside Canvas)
 * Renders different layouts for mobile and desktop
 */
function AudioController() {
  const {
    audioPlaying,
    audioVolume,
    audioData,
    currentChapter,
    toggleAudio,
    setAudioVolume
  } = useAppStore();

  const { isMobileOrTablet } = useMobile();

  // Calculate frequency bands from FFT data
  const { bass, mids, highs, amplitude } = useMemo(() => {
    if (!audioData || audioData.length === 0) {
      return { bass: 0, mids: 0, highs: 0, amplitude: 0 };
    }

    const bassSum = audioData.slice(0, 5).reduce((a, b) => a + b, 0) / 5;
    const midsSum = audioData.slice(5, 21).reduce((a, b) => a + b, 0) / 16;
    const highsSum = audioData.slice(21, 64).reduce((a, b) => a + b, 0) / 43;
    const amp = audioData.reduce((a, b) => a + b, 0) / audioData.length;

    return {
      bass: bassSum,
      mids: midsSum,
      highs: highsSum,
      amplitude: amp
    };
  }, [audioData]);

  // Handle play/pause click
  const handlePlayPause = () => {
    toggleAudio();
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setAudioVolume(newVolume);
  };

  // Get current track name
  const track = currentChapter !== null ? getAudioTrack(currentChapter + 1) : null;
  const currentTrackName = track ? track.display : 'Overview';

  const isAnalyzing = audioPlaying && amplitude > 0.01;

  // Render mobile layout
  if (isMobileOrTablet) {
    return (
      <MobileAudioController
        audioPlaying={audioPlaying}
        isAnalyzing={isAnalyzing}
        bass={bass}
        mids={mids}
        highs={highs}
        amplitude={amplitude}
        onPlayPause={handlePlayPause}
      />
    );
  }

  // Desktop layout
  return (
    <div className="audio-controller">
      {/* Play/Pause Button */}
      <button
        className="audio-play-btn"
        onClick={handlePlayPause}
        title={audioPlaying ? 'Pause' : 'Play'}
      >
        {audioPlaying ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </button>

      {/* Track Info */}
      <div className="audio-track-info">
        <div className="audio-track-name">{currentTrackName}</div>
        <div className="audio-status">
          {audioPlaying && !isAnalyzing ? (
            <span className="audio-loading">Loading...</span>
          ) : audioPlaying ? (
            <span className="audio-ready">Playing</span>
          ) : (
            <span className="audio-ready">Paused</span>
          )}
        </div>
      </div>

      {/* Volume Control */}
      <div className="audio-volume">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
        </svg>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={audioVolume}
          onChange={handleVolumeChange}
          className="audio-volume-slider"
          aria-label="Volume"
        />
        <span className="audio-volume-value">{Math.round(audioVolume * 100)}%</span>
      </div>

      {/* Audio Visualizer - always rendered for consistent size, hidden when not active */}
      <AudioVisualizer
        bass={isAnalyzing ? bass : 0}
        mids={isAnalyzing ? mids : 0}
        highs={isAnalyzing ? highs : 0}
        amplitude={isAnalyzing ? amplitude : 0}
        isHidden={!isAnalyzing}
      />
    </div>
  );
}

export default AudioController;
